import { Button, Dropdown, type MenuProps, Tooltip } from 'antd';
import clsx from 'clsx';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import type File from '../../../interfaces/File';
import { Icon } from '../../Core/Icon';
import ShortcutHint from '../../Common/ShortcutHint';
import type FileAction from '../../../interfaces/FileAction';

/**
 * Props:
 * - handleFileMenuAction: Callback for when a file menu action is triggered (ids, action)
 * - files: List of files
 * - className: Optional className for styling the actions container
 * - filesActions: Array of available file actions
 */
interface Props {
  handleFileMenuAction: (ids: number[], action: FileMenuActionEnum) => void;
  files: File[];
  className?: string;
  filesActions: FileAction[];
}

/**
 * FilesActions component
 * Renders a list of file actions, with overflow managed responsively and
 * excess actions shown in a dropdown ("More") menu.
 */
const FilesActions: React.FC<Props> = ({
  handleFileMenuAction,
  files,
  className,
  filesActions,
}) => {
  const MIN_VALID_ACTION_WIDTH = 24;
  const { t } = useTranslation();

  // Ref for the main container wrapping all action buttons
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref for the overflow dropdown button ("More" button)
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  // Map of refs for each action button, keyed by their action key
  const actionButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Index at which visible actions overflow and should move to the dropdown (0-based)
  const [overflowStart, setOverflowStart] = useState(0);

  /**
   * Renders the label for actions, translating if it's a string.
   * @param label - Can be string (to be translated) or ReactNode.
   */
  const renderLabel = (label: FileAction['label']) =>
    typeof label === 'string' ? t(label) : label;

  // Extracts file IDs from the files array (dependency for action handlers)
  const fileIds = useMemo(() => files.map((f) => f.id), [files]);

  /**
   * Stores refs to visible action buttons.
   * @param key - Action key (string)
   */
  const setActionButtonRef = useCallback(
    (key: string) => (element: HTMLButtonElement | null) => {
      if (element) {
        actionButtonRefs.current.set(key, element);
      }
    },
    [],
  );

  // Compute actions that should be shown in the overflow dropdown
  const overflowActions = useMemo(
    () => filesActions.slice(overflowStart),
    [filesActions, overflowStart],
  );

  /**
   * Measures all action buttons and determines how many fit in the container,
   * setting overflowStart to the correct index.
   * Called initially and on resize.
   */
  const measureItems = useCallback(() => {
    try {
      const container = containerRef.current;
      const overflowBtn = overflowButtonRef.current;
      const totalActions = filesActions.length;
      const refsCount = actionButtonRefs.current.size;

      // If no container, no actions, or not all refs are assigned, just overflow everything
      if (!container || totalActions === 0 || refsCount < totalActions) {
        setOverflowStart(totalActions);
        return;
      }

      // Calculate the available width (with 20px safety margin)
      const containerWidth = typeof container.clientWidth === 'number' ? container.clientWidth : 0;
      const availableWidth = Math.max(0, containerWidth - 20);

      // Calculate overflow button width (dropdown) with fallback
      let overflowBtnWidth = 0;
      if (overflowBtn) {
        try {
          const styleO = window.getComputedStyle(overflowBtn);
          const offset = typeof overflowBtn.offsetWidth === 'number' ? overflowBtn.offsetWidth : 0;
          const marginRight = Number.parseFloat(styleO.marginRight || '0') || 0;
          overflowBtnWidth = offset + marginRight;
        } catch {
          overflowBtnWidth = 32; // Reasonable fallback
        }
      }

      let usedWidth = 0;
      let cutIndex = totalActions;

      // Try to fit as many action buttons as possible; overflow the rest.
      for (let i = 0; i < totalActions; i++) {
        const action = filesActions[i];
        const mapKey = typeof action?.key === 'string' ? action.key : '';
        const btn = actionButtonRefs.current.get(mapKey);

        // If either ref missing, stop fitting here
        if (!btn) {
          cutIndex = i;
          break;
        }

        let btnWidth = 0;
        try {
          // Try retrieving previously calculated width from dataset
          const saved = btn.dataset?.calculatedWidth;
          if (typeof saved === 'string' && /^\d+(\.\d+)?$/.test(saved)) {
            btnWidth = Number.parseFloat(saved);
            if (isNaN(btnWidth) || btnWidth < MIN_VALID_ACTION_WIDTH) {
              throw new Error('Bad cached width');
            }
          } else {
            // Measure using the wrapper when available; hidden class is applied to wrapper div.
            // If wrapper is hidden, temporarily unhide it to get a real width.
            const wrapper = btn.parentElement as HTMLElement | null;
            const measureTarget = wrapper ?? btn;
            let wasHidden = false;
            if (measureTarget.classList.contains('hidden')) {
              measureTarget.style.display = 'flex';
              wasHidden = true;
            }
            const styleB = window.getComputedStyle(measureTarget);
            const width = measureTarget.getBoundingClientRect().width;
            const marginRight = Number.parseFloat(styleB.marginRight || '0') || 0;
            const borderLeft = Number.parseFloat(styleB.borderLeftWidth || '0') || 0;
            const borderRight = Number.parseFloat(styleB.borderRightWidth || '0') || 0;
            btnWidth = width + marginRight + borderLeft + borderRight;

            // Cache the calculated width for next time (improves perf)
            if (!isNaN(btnWidth) && btnWidth > 0) {
              btn.dataset.calculatedWidth = btnWidth.toString();
            }
            if (wasHidden) {
              measureTarget.style.display = '';
            }
          }
        } catch {
          // Fallback: use a minimum width for safety
          btnWidth = 48;
        }

        // Only reserve space for the dropdown if there's an item after this
        const needsOverflowBtn = i < totalActions - 1;
        const nextTotal = Math.round(
          usedWidth + btnWidth + (needsOverflowBtn ? overflowBtnWidth : 0),
        );

        if (nextTotal >= availableWidth) {
          cutIndex = i;
          break;
        }

        usedWidth += btnWidth;
      }
      setOverflowStart(cutIndex);
    } catch {
      // Defensive: If measuring fails, overflow all actions as a fallback
      setOverflowStart(filesActions.length);
    }
  }, [filesActions]);

  /**
   * Sets up ResizeObserver to monitor the container and re-measure on resize.
   * Also triggers initial measurement after mount or list change.
   */
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Observe container size changes and re-measure
    const observer = new ResizeObserver(() => {
      measureItems();
    });
    observer.observe(container);

    // Initial measurement to set up overflow based on current DOM state
    if (filesActions.length > 0) {
      measureItems();
    } else {
      setOverflowStart(0); // No actions, show nothing
    }

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
    };
  }, [measureItems, filesActions]);

  /**
   * Recursively builds Ant Design menu items from the FileActions structure.
   * Each action can be a single menu item or a submenu (if it has children).
   */
  const getItems = useCallback(
    (actions: FileAction[]): MenuProps['items'] =>
      actions.map((a) => {
        const has = a.children?.length;
        return {
          extra: a.shortcutId ? <ShortcutHint shortcutId={a.shortcutId} /> : undefined,
          key: a.key,
          icon: a.icon,
          label: renderLabel(a.label),
          danger: a.danger,
          disabled: a.disabled,
          children: has ? getItems(a.children!) : undefined,
          onClick: () => {
            if (!has) handleFileMenuAction(fileIds, a.key as FileMenuActionEnum);
          },
        };
      }),
    [fileIds, handleFileMenuAction, t],
  );

  // If no actions at all, show a placeholder message
  if (!filesActions.length) {
    return <span className={clsx('grow px-2', className)}>{t('common.noActionsAvailable')}</span>;
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex flex-1 items-center gap-1 overflow-hidden whitespace-nowrap',
        className,
      )}
      style={{ minWidth: 0 }}
    >
      {filesActions.map((a, index) => {
        // Only show actions that fit (before overflowStart); the rest go in Dropdown
        const isVisible = index <= overflowStart - 1;

        // Dropdown for actions with children (submenus)
        if (a.children && a.children.length > 0) {
          return (
            <div key={a.key} className={clsx('flex items-center gap-2', { hidden: !isVisible })}>
              <Tooltip title={t(String(a.label))}>
                <Dropdown menu={{ items: getItems(a.children) }} trigger={['hover']}>
                  <Button
                    ref={setActionButtonRef(a.key)}
                    type="text"
                    disabled={a.disabled}
                    danger={a.danger}
                  >
                    {a.icon}
                    {renderLabel(a.label)}
                  </Button>
                </Dropdown>
              </Tooltip>
            </div>
          );
        }
        // Plain button for regular actions
        return (
          <div key={a.key} className={clsx('flex items-center gap-2', { hidden: !isVisible })}>
            <Tooltip title={t(String(a.label))}>
              <Button
                ref={setActionButtonRef(a.key)}
                type="text"
                disabled={a.disabled}
                danger={a.danger}
                onClick={() => handleFileMenuAction(fileIds, a.key as FileMenuActionEnum)}
              >
                {a.icon}
                {renderLabel(a.label)}
              </Button>
            </Tooltip>
          </div>
        );
      })}

      {/* The "More..." overflow button and dropdown */}
      <div
        ref={overflowButtonRef as any}
        style={{
          visibility: filesActions.length > overflowStart ? 'visible' : 'hidden',
        }}
        className="flex items-center"
      >
        <Dropdown menu={{ items: getItems(overflowActions) }} trigger={['click']}>
          <Button type="text" aria-label={t('a11y.openFileMenu')}>
            <Icon name="EllipsisVerticalIcon" className="w-5" />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

export default FilesActions;
